/**
 * Copy data from one object to another using the `keysTable` to map the key
 * names.
 * @arg {Object<string, string>} keysTable
 * @arg {Object<string, string>} from
 * @arg {Object} to
 * @return {Object}
 */
export function copyAndMapObj(keysTable, from, to) {
  // Check if object is of type FormData and if it it
  // map it to an object.
  if (from instanceof FormData) {
    if (from == null) return {};
    /** @type {Record<string,any>} */
    const fromObj = {};
    for (const [key, val] of from.entries()) {
      fromObj[key] = val;
    }
    from = fromObj;
  }

  return Object.keys(keysTable).reduce(function (
    /** @type {Record<string,any>} */ result,
    srcKey,
  ) {
    var destKey = /** @type {string} */ (keysTable[srcKey]);
    if (from[srcKey]) result[destKey] = from[srcKey];
    return result;
  }, to);
}

/**
 * @typedef {string | string[]} FieldValue
 * @typedef {Object<string, FieldValue>} FormAsObject
 */

/**
 * Field types excluded from serialization. This is a set, the values are not
 * used, only the keys.
 * @type {Object<string, 0>}
 */
var xFieldTypes = { file: 0, reset: 0, submit: 0, button: 0 };

/**
 * Serializes the values of an HTML form element into an {name: value}
 * Object. Where `name` is a string and `value` is either a string or an array
 * of strings.
 * @arg  {HTMLFormElement} form The form to serialize
 * @return {FormAsObject} The serialized form data
 */
export function serializeFormAsObject(form) {
  /** @type {FormAsObject} */
  var formObj = {};
  var elements = form.elements;
  var field;
  /** @type {HTMLSelectElement} */
  var select;
  /** @type {HTMLOptionsCollection} */
  var options;
  /** @type {HTMLOptionElement} */
  var option;
  for (var i = 0, len = elements.length; i < len; i += 1) {
    field = elements[i];
    if (
      field instanceof HTMLInputElement ||
      field instanceof HTMLSelectElement
    ) {
      // Don't serialize fields without a name, submits, buttons, file and reset inputs, and disabled fields
      if (!field.name || field.disabled || field.type in xFieldTypes) continue;

      // If a multi-select, get all selections
      if (field.type === "select-multiple") {
        /** @type {HTMLSelectElement} */
        select = field;
        options = select.options;
        for (var j = 0, len2 = options.length; j < len2; j += 1) {
          option = options[j];
          if (!option.selected) continue;
          formObj = extendFormObject(formObj, select.name, option.value);
        }

        // All other form elements; Checkboxes and radios only is they are selected
      } else if (
        (field.type !== "checkbox" && field.type !== "radio") ||
        /** @type {HTMLInputElement} */
        (field).checked
      ) {
        formObj = extendFormObject(formObj, field.name, field.value);
      }
    }
  }

  return formObj;
}

var owns = Object.prototype.hasOwnProperty;

/**
 * @arg {FormAsObject} formObj
 * @arg {string} name
 * @arg {string} value
 * @return {FormAsObject}
 */
function extendFormObject(formObj, name, value) {
  /** @type {FieldValue} */
  var existingValue;
  if (owns.call(formObj, name)) {
    existingValue = formObj[name];
    if (Array.isArray(existingValue)) existingValue.push(value);
    else formObj[name] = [existingValue, value];
  } else formObj[name] = value;
  return formObj;
}

/**
 * Recursively checks that an object has at least one value associated with a
 * key.
 * @example
 * objectHasValues({a:''}) // false
 * @example
 * objectHasValues({a:{b:''}}) // false
 * @example
 * objectHasValues({a:{b:{c:{d:'\t'}, e:[NaN, undefined, null, '\n', '\r']}}}) // false
 * @example
 * objectHasValues({a:['yes', true, false, 0]}) // true
 * @arg {*} obj
 * @return {boolean}
 */
export function objectHasValues(obj) {
  if (obj == null) return false;
  if (typeof obj === "string") return obj.trim().length > 0;
  if (typeof obj === "number") return !isNaN(obj);
  if (typeof obj !== "object") return true;
  if (Array.isArray(obj)) {
    for (var i = 0, len = obj.length; i < len; i += 1) {
      if (objectHasValues(obj[i])) return true;
    }
    return false;
  } else {
    for (var key in obj) {
      if (owns.call(obj, key) && objectHasValues(obj[key])) return true;
    }
    return false;
  }
}

/**
 * Returns the Google Analytics Client Id from the cookie. If the cookie does
 * not exist, returns undefined.
 * @return {string | undefined}
 */
export function getGaClientId() {
  try {
    return document.cookie
      .match(/_ga=(.+?);/)[1]
      .split(".")
      .slice(-2)
      .join(".");
  } catch (e) {
    return undefined;
  }
}

/**
 * getUTCTimeZone
 * Returns an object that describes the users browser timezone settings and
 * represents it in UTC. This way analytics can do queries on regions without
 * needing to worry about timezone differences.
 *
 * @arg {number} [overrideOffset] used for unit testing, if this is set, the
 * offset will not be queried from the browser
 */
export function getUTCTimeZone(overrideOffset) {
  // Used this timezone listing as a reference:
  // https://en.wikipedia.org/wiki/List_of_tz_database_time_zones

  // Offset is in minutes so turn into hours
  // If overriding the browser offset, some examples of
  // offset might be: 240, -330, -345, 660, 0
  let utcOffset = 0;
  if (overrideOffset === undefined) {
    utcOffset = new Date().getTimezoneOffset() / 60;
  } else {
    utcOffset = overrideOffset / 60;
  }

  // Reverse sign because getTimezoneOffset() returns the difference in minutes
  // from local time to UTC. See for details
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset#Description
  utcOffset = -utcOffset;

  // Determine the sign
  const sign = utcOffset >= 0 ? "+" : "-";

  // Offset in postive integer form
  utcOffset = Math.abs(utcOffset);

  // Hour
  let hour = Math.floor(utcOffset);

  // Pad hour to 2 chars
  if (hour < 10) {
    hour = "0" + hour;
  }

  // Minutes can be 0, 30, 45
  // Get the remainder and if 0, pad to 2 chars,
  // otherwise multiply it by 60 minutes to get 30, or 45 minutes.
  // Additionally make sure that minutes has a char len of 2 chars
  let minutes = utcOffset % 1;
  if (minutes === 0) {
    minutes = "0" + minutes;
  } else {
    minutes = 60 * minutes;
  }

  const utcString = "UTC" + sign + hour + ":" + minutes;

  return {
    timeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone, // America/Toronto
    timeZone: utcString, // UTC-04:00
  };
}
